import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { CalendarDays, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { CalendarGrid } from "@/components/app/CalendarPopup";
import { TIME_OPTIONS, formatTime } from "@/components/app/TimePopup";
import { usePopoverBehavior } from "@/hooks/usePopoverBehavior";
import { formatEventDateTime } from "@/lib/timeHelpers";

// Round a Date to the nearest 15-min slot → "HH:MM"
function roundToSlot(date) {
  if (!date) return null;
  const h   = date.getHours();
  const m   = Math.round(date.getMinutes() / 15) * 15;
  const h2  = m >= 60 ? (h + 1) % 24 : h;
  const m2  = m >= 60 ? 0 : m;
  return `${String(h2).padStart(2, "0")}:${String(m2).padStart(2, "0")}`;
}

/**
 * DateTimePopup — combined date + time picker in a single popover.
 *
 * Props:
 *   value        (Date | null) — committed value
 *   onChange     (Date | null) => void
 *   defaultValue (Date | null) — picker's initial position when value is null
 *   placeholder  string
 *   id           string
 *   error        bool
 *   disabled     bool
 *   clearable    bool — show × button when a value is set
 */
export default function DateTimePopup({
  value,
  onChange,
  defaultValue = null,
  placeholder  = "Pick a date & time",
  id,
  error    = false,
  disabled = false,
  clearable = false,
}) {
  const { open, setOpen, openPopover: openPopoverBase, popoverStyle, triggerRef, popoverRef } =
    usePopoverBehavior();
  // pickerDate tracks what's highlighted inside the open popover
  const [pickerDate, setPickerDate] = useState(null);
  const [viewYear,   setViewYear]   = useState(() => new Date().getFullYear());
  const [viewMonth,  setViewMonth]  = useState(() => new Date().getMonth());
  const selectedTimeRef = useRef(null);

  function openPopover() {
    if (disabled) return;
    const initial = value ?? defaultValue ?? new Date();
    setPickerDate(initial);
    setViewYear(initial.getFullYear());
    setViewMonth(initial.getMonth());
    openPopoverBase();
  }

  // Scroll the highlighted time into view each time the popover opens
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => selectedTimeRef.current?.scrollIntoView({ block: "center" }), 0);
    return () => clearTimeout(t);
  }, [open]);

  function handleDaySelect(date) {
    // Preserve time from pickerDate; default to noon if none yet
    const h = pickerDate ? pickerDate.getHours()   : 12;
    const m = pickerDate ? pickerDate.getMinutes() : 0;
    const newDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), h, m);
    setPickerDate(newDate);
    onChange(newDate);
    // Stay open so user can also adjust the time
  }

  function handleTimeSelect(hhmm) {
    const [h, m] = hhmm.split(":").map(Number);
    const base   = pickerDate ?? new Date();
    const newDate = new Date(base.getFullYear(), base.getMonth(), base.getDate(), h, m);
    setPickerDate(newDate);
    onChange(newDate);
    setOpen(false);
  }

  const selectedSlot = roundToSlot(pickerDate);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        onClick={openPopover}
        disabled={disabled}
        className={cn(
          "flex h-10 w-full items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm text-left transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
          error ? "border-destructive" : "border-input",
          !value && "text-muted-foreground",
        )}
      >
        <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="flex-1 truncate">{value ? formatEventDateTime(value) : placeholder}</span>
        {clearable && value ? (
          <X
            className="h-3.5 w-3.5 shrink-0 text-muted-foreground hover:text-foreground transition-colors"
            onClick={(e) => { e.stopPropagation(); onChange(null); }}
          />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground opacity-60" />
        )}
      </button>

      {open && createPortal(
        <div
          ref={popoverRef}
          style={popoverStyle}
          className="flex rounded-xl border border-border bg-popover shadow-lg overflow-hidden select-none"
        >
          {/* Calendar */}
          <div className="p-3 border-r border-border">
            <CalendarGrid
              viewYear={viewYear}
              viewMonth={viewMonth}
              onViewChange={(y, m) => { setViewYear(y); setViewMonth(m); }}
              onDaySelect={handleDaySelect}
              selectedDate={pickerDate}
              mode="single"
              rangeValue={null}
            />
          </div>

          {/* Time list */}
          <ul className="w-28 max-h-[280px] overflow-y-auto py-1">
            {TIME_OPTIONS.map((time) => {
              const isSel = time === selectedSlot;
              return (
                <li key={time}>
                  <button
                    ref={isSel ? selectedTimeRef : null}
                    type="button"
                    onClick={() => handleTimeSelect(time)}
                    className={cn(
                      "w-full text-left px-3 py-1.5 text-sm transition-colors",
                      isSel ? "bg-primary text-primary-foreground" : "hover:bg-muted",
                    )}
                  >
                    {formatTime(time)}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>,
        document.body,
      )}
    </>
  );
}
